import { Field, Form } from 'react-final-form'

import Button from 'common/components/button'
import FormItem from 'common/components/FormItem'
import Input from 'common/components/Input'
import Modal from 'common/components/Modal'
import SingleSelect from 'common/components/singleSelect'
import Table, { Column } from 'common/components/table'
import Emoji from 'common/emoji'

type Props = {
  isOpen: boolean
  fileName: string
  data: string[][]
  headers: string[]
  onCancel: () => void
  onImport: (values: { fileName: string; customerIdColumn: string }) => void
}
const validataFileName = (value: string | undefined) =>
  value?.trim() === undefined || value.trim().length === 0
    ? 'Required'
    : undefined

const validateCustomerIdColumn = (value: string | undefined) =>
  value === undefined ? 'Required' : undefined

const CsvCustomerPreview = ({
  isOpen,
  fileName,
  data,
  headers,
  onCancel,
  onImport,
}: Props) => {
  const columns: Column[] = headers.map((value) => ({
    Header: value,
    accessor: value,
    width: 200,
  }))
  const handleSave = (values: {
    fileName: string
    customerIdColumn: string
  }) => {
    onImport(values)
  }

  return (
    <Form<{ fileName: string; customerIdColumn: string }>
      onSubmit={handleSave}
      initialValues={{
        fileName: fileName.substring(0, fileName.lastIndexOf('.')) || fileName,
      }}
      render={({ handleSubmit, hasValidationErrors }) => {
        return (
          <Modal
            visible={isOpen}
            centered
            onClose={() => {
              onCancel()
            }}
            isResponsive
            className="block"
            style={{ maxWidth: '80vw' }}
            footer={
              <div className="my-3">
                <Button
                  data-cy="cancel-button"
                  className="mr-4 pointer-events-auto"
                  ghost
                  onClick={() => {
                    onCancel()
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={(event) => {
                    handleSubmit(event)
                  }}
                  disabled={hasValidationErrors}
                >
                  Import
                </Button>
              </div>
            }
          >
            <div className="text-2xl flex flex-grow-0 flex-row gap-4 mb-6">
              <Emoji symbol="🤩" />
              <div className="font-bold">{fileName}</div>
            </div>

            <form onSubmit={handleSubmit} className="text-sm flex gap-6">
              <Field<string | undefined>
                name="fileName"
                validate={validataFileName}
                render={({ input, meta }) => (
                  <FormItem
                    className="max-w-100"
                    label="Data source name"
                    guidingText="This will be displayed in the data source table."
                    htmlFor="fileName"
                    error={meta.error}
                  >
                    <Input
                      type="text"
                      id="fileName"
                      variant="default"
                      className="text-coolGray-800"
                      value={input.value}
                      name={input.name}
                      onChange={input.onChange}
                      maxLength={50}
                      required
                    />
                  </FormItem>
                )}
              />
              <Field<string>
                name="customerIdColumn"
                validate={validateCustomerIdColumn}
                render={({ input, meta }) => (
                  <FormItem
                    label="Customer ID column"
                    htmlFor="customerIdColumn"
                    className="max-w-75"
                    error={meta.error}
                  >
                    <SingleSelect
                      id="customerIdColumn"
                      onChange={(val) => input.onChange(val?.value)}
                      value={input.value}
                      isSearchable={true}
                      required
                      options={headers.map((value) => ({
                        label: value,
                        value,
                      }))}
                    />
                  </FormItem>
                )}
              />
            </form>

            <Table columns={columns} data={data} />
          </Modal>
        )
      }}
    />
  )
}

const CsvCustomerPreviewWithResetState = (props: Props) => (
  <CsvCustomerPreview key={`${props.isOpen}`} {...props} />
)
export default CsvCustomerPreviewWithResetState
